import React from 'react';
import {
  FaSignInAlt,
  FaTwitch,
  FaSpotify,
  FaRocket,
  FaMusic,
  FaHeadphones,
  FaBolt,
  FaArrowRight,
  FaTachometerAlt,
} from 'react-icons/fa';
import { HOME_TEXTS } from './constants';
import '../../styles/Home.css';

/**
 * Section Hero de la page d'accueil
 * Affiche le titre principal, la description et les actions de connexion
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onOpenLogin - Fonction pour ouvrir la modal de connexion
 * @param {Function} [props.onGoToDashboard] - Navigation vers le tableau de bord (session active)
 * @param {boolean} props.isAuthenticated - Indique si l'utilisateur est connecté
 * @returns {JSX.Element} Le composant Hero
 */
export default function HomeHero({
  onOpenLogin,
  onGoToDashboard,
  isAuthenticated,
}) {
  return (
    <div className="home-hero">
      {/* Particules décoratives */}
      <div className="hero-particles">
        <span className="particle particle-1"></span>
        <span className="particle particle-2"></span>
        <span className="particle particle-3"></span>
        <span className="particle particle-4"></span>
        <span className="particle particle-5"></span>
        <span className="particle particle-6"></span>
      </div>

      {/* Badges flottants */}
      <div className="hero-floating-badges">
        <div className="floating-badge twitch">
          <FaTwitch />
        </div>
        <div className="floating-badge spotify">
          <FaSpotify />
        </div>
        <div className="floating-badge music">
          <FaMusic />
        </div>
      </div>

      {/* Contenu principal */}
      <div className="hero-content">
        <div className="hero-badge">
          <FaRocket className="hero-badge-icon" />
          <span>StreamingBot</span>
        </div>

        <h1>
          <span className="hero-title-line">Streamez </span>
          <span className="hero-title-highlight">
            comme <span className="hero-gradient-text">un PRO</span>
          </span>
          <span className="hero-title-line"> avec Twitch et Spotify</span>
        </h1>

        <p className="hero-description">
          Boostez vos streams avec des outils pensés pour les créateurs.
        </p>

        {/* Features highlights */}
        <div className="hero-features">
          <div className="hero-feature-item">
            <div className="hero-feature-icon twitch">
              <FaBolt />
            </div>
            <span>Commandes chat</span>
          </div>
          <div className="hero-feature-divider"></div>
          <div className="hero-feature-item">
            <div className="hero-feature-icon spotify">
              <FaHeadphones />
            </div>
            <span>Contrôle musique</span>
          </div>
          <div className="hero-feature-divider"></div>
          <div className="hero-feature-item">
            <div className="hero-feature-icon cyan">
              <FaMusic />
            </div>
            <span>Temps réel</span>
          </div>
        </div>

        {!isAuthenticated && (
          <div className="hero-actions">
            <button className="login-hero-btn primary" onClick={onOpenLogin}>
              <FaSignInAlt />
              <span>Commencer gratuitement</span>
              <FaArrowRight className="btn-arrow" />
              <div className="btn-shine"></div>
            </button>

            <p className="hero-sub-cta">
              Connectez-vous avec Twitch ou Spotify pour démarrer
            </p>

            <div className="hero-platforms">
              <span className="platform-chip twitch">
                <FaTwitch />
                Twitch
              </span>
              <span className="platform-chip spotify">
                <FaSpotify />
                Spotify
              </span>
            </div>
          </div>
        )}

        {isAuthenticated && (
          <div className="hero-authenticated">
            <div className="authenticated-badge">
              <span className="pulse-dot"></span>
              <span>Session active — Bienvenue !</span>
            </div>
            {onGoToDashboard && (
              <button
                type="button"
                className="login-hero-btn primary hero-dashboard-btn"
                onClick={onGoToDashboard}
                aria-label={HOME_TEXTS.APPS_SECTION.DASHBOARD_TITLE}
              >
                <FaTachometerAlt />
                <span>{HOME_TEXTS.HERO_OPEN_DASHBOARD}</span>
                <FaArrowRight className="btn-arrow" />
                <div className="btn-shine"></div>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Décoration de fond */}
      <div className="hero-decoration">
        <div className="decoration-ring ring-1"></div>
        <div className="decoration-ring ring-2"></div>
        <div className="decoration-ring ring-3"></div>
      </div>
    </div>
  );
}
